import axios from 'axios';

const api = axios.create({
  baseURL: '/',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    return Promise.reject(err);
  },
);

export interface EmailAccount {
  id: number;
  user_id: number;
  label: string;
  email: string;
  display_name?: string;
  imap_host: string;
  imap_port: number;
  imap_ssl: boolean;
  smtp_host: string;
  smtp_port: number;
  smtp_ssl: boolean;
  smtp_starttls: boolean;
  is_active: boolean;
  is_default: boolean;
  last_sync_at?: string;
  last_sync_status?: string;
  last_sync_error?: string;
  sync_interval_seconds: number;
  oauth_provider?: string | null;
  signature_html?: string | null;
  signature_text?: string | null;
}

export interface EmailFolder {
  name: string;
  raw: string;
  delim: string;
  special?: string; // inbox | sent | drafts | trash | junk
  flags: string[];
  total?: number | null;
  unseen?: number | null;
}

export interface EmailMessageRow {
  id: number;
  account_id: number;
  folder: string;
  imap_uid: number;
  message_id?: string;
  subject?: string;
  from_addr?: string;
  to_addrs?: string;
  cc_addrs?: string;
  snippet?: string;
  has_attachments: boolean;
  is_seen: boolean;
  is_flagged: boolean;
  is_answered: boolean;
  is_draft: boolean;
  date?: string;
  size_bytes?: number;
}

export interface EmailMessageFull extends EmailMessageRow {
  body_text?: string;
  body_html?: string;
  bcc_addrs?: string;
  reply_to?: string;
  in_reply_to?: string;
  references?: string;
  attachments: EmailAttachment[];
}

export interface EmailAttachment {
  id: number;
  message_id: number;
  filename?: string;
  mime_type?: string;
  size_bytes?: number;
  content_id?: string;
}

export interface ListMessagesResponse {
  folder: string;
  page: number;
  page_size: number;
  total: number;
  items: EmailMessageRow[];
}

export interface AutodetectResult {
  imap_host: string;
  imap_port: number;
  imap_ssl: boolean;
  smtp_host: string;
  smtp_port: number;
  smtp_ssl: boolean;
  smtp_starttls: boolean;
}

export interface AccountCreatePayload {
  email: string;
  password: string;
  label?: string;
  display_name?: string;
  imap_host?: string;
  imap_port?: number;
  imap_ssl?: boolean;
  smtp_host?: string;
  smtp_port?: number;
  smtp_ssl?: boolean;
  smtp_starttls?: boolean;
  sync_interval_seconds?: number;
  is_default?: boolean;
  signature_html?: string | null;
  signature_text?: string | null;
}

export interface SendPayload {
  account_id: number;
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body_text?: string;
  body_html?: string;
  in_reply_to_message_id?: number;
  attachments_b64?: Array<{ filename: string; mime_type: string; data_b64: string }>;
}

export const emailApi = {
  // accounts
  listAccounts: () => api.get<EmailAccount[]>('/api/email/accounts').then((r) => r.data),
  autodetect: (email: string) => api.post<AutodetectResult>('/api/email/accounts/autodetect', { email }).then((r) => r.data),
  createAccount: (payload: AccountCreatePayload) => api.post<EmailAccount>('/api/email/accounts', payload).then((r) => r.data),
  updateAccount: (id: number, payload: Partial<AccountCreatePayload> & { is_active?: boolean }) =>
    api.put<EmailAccount>(`/api/email/accounts/${id}`, payload).then((r) => r.data),
  deleteAccount: (id: number) => api.delete<{ ok: boolean }>(`/api/email/accounts/${id}`).then((r) => r.data),
  testAccount: (id: number) =>
    api.post<{ imap: { ok: boolean; message: string }; smtp: { ok: boolean; message: string }; ok: boolean }>(
      `/api/email/accounts/${id}/test`,
    ).then((r) => r.data),
  // folders + sync
  listFolders: (accountId: number) => api.get<EmailFolder[]>(`/api/email/accounts/${accountId}/folders`).then((r) => r.data),
  syncFolder: (accountId: number, folder = 'INBOX', limit = 50, beforeUid?: number) => {
    const params = new URLSearchParams({ folder, limit: String(limit) });
    if (beforeUid) params.set('before_uid', String(beforeUid));
    return api
      .post<{ folder: string; fetched: number; added: number; existing: number; oldest_uid?: number; total_in_folder?: number }>(
        `/api/email/accounts/${accountId}/sync?${params}`,
      )
      .then((r) => r.data);
  },
  // messages
  listMessages: (
    accountId: number,
    opts: { folder?: string; page?: number; page_size?: number; search?: string; unread_only?: boolean } = {},
  ) => {
    const params = new URLSearchParams();
    if (opts.folder) params.set('folder', opts.folder);
    if (opts.page) params.set('page', String(opts.page));
    if (opts.page_size) params.set('page_size', String(opts.page_size));
    if (opts.search) params.set('search', opts.search);
    if (opts.unread_only) params.set('unread_only', 'true');
    return api.get<ListMessagesResponse>(`/api/email/accounts/${accountId}/messages?${params}`).then((r) => r.data);
  },
  getMessage: (messageId: number) => api.get<EmailMessageFull>(`/api/email/messages/${messageId}`).then((r) => r.data),
  setFlag: (messageId: number, flag: { seen?: boolean; flagged?: boolean }) =>
    api.post<{ ok: boolean }>(`/api/email/messages/${messageId}/flag`, flag).then((r) => r.data),
  deleteMessage: (messageId: number) =>
    api.delete<{ ok: boolean }>(`/api/email/messages/${messageId}`).then((r) => r.data),
  attachmentUrl: (messageId: number, attId: number) =>
    `/api/email/messages/${messageId}/attachments/${attId}`,
  // send
  send: (payload: SendPayload) => api.post<{ ok: boolean; message_id: string }>('/api/email/send', payload).then((r) => r.data),
  sendMultipart: (params: {
    account_id: number;
    to: string;
    cc?: string;
    bcc?: string;
    subject: string;
    body_text?: string;
    body_html?: string;
    in_reply_to_message_id?: number;
    files: File[];
  }) => {
    const form = new FormData();
    form.append('account_id', String(params.account_id));
    form.append('to', params.to);
    if (params.cc) form.append('cc', params.cc);
    if (params.bcc) form.append('bcc', params.bcc);
    form.append('subject', params.subject);
    if (params.body_text != null) form.append('body_text', params.body_text);
    if (params.body_html != null) form.append('body_html', params.body_html);
    if (params.in_reply_to_message_id) form.append('in_reply_to_message_id', String(params.in_reply_to_message_id));
    params.files.forEach((f) => form.append('files', f));
    return api
      .post<{ ok: boolean; message_id: string }>('/api/email/send-multipart', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
  // drafts
  saveDraft: (payload: {
    account_id: number;
    to?: string;
    cc?: string;
    bcc?: string;
    subject?: string;
    body_text?: string;
    body_html?: string;
  }) => api.post<{ ok: boolean; folder: string }>('/api/email/drafts', payload).then((r) => r.data),
  // OAuth
  yandexOAuthStart: () =>
    api.get<{ authorize_url: string; state: string }>('/api/email/oauth/yandex/start').then((r) => r.data),
};
